import { Save, Search, Trash2 } from 'lucide-react'
import { AP_INVOICE_PRIORITIES, AP_INVOICE_REVIEW_QUEUES, AP_INVOICE_STATUSES } from '../constants'
import type { ApInvoiceAssignedUser, ApInvoiceReviewQueueKey } from '../types'
import { formatInvoicePriority, formatInvoiceStatus } from '../status'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SearchSelect } from '@/components/search-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ListFilter, ListFilters } from '@/components/list-filters'
import type {
  AssigneeFilterValue,
  IntakeSourceFilterValue,
  LinkFilterValue,
  useInvoiceListFilters,
} from './use-invoice-list-filters'
import { SearchInput } from '@/components/search-input'
import { InlineFieldInput } from '@/components/inline-field-group'

function AssigneeSelect({
  value,
  onValueChange,
  staff,
  disabled,
}: {
  value: AssigneeFilterValue
  onValueChange: (value: AssigneeFilterValue) => void
  staff: ApInvoiceAssignedUser[]
  disabled?: boolean
}) {
  return (
    <SearchSelect
      ariaLabel="Invoice assignee filter"
      className="h-9 w-full"
      value={value === 'all' ? undefined : value}
      allLabel="All assignees"
      options={[
        { value: 'unassigned', label: 'Unassigned' },
        ...staff.map((user) => ({ value: `staff:${user.id}`, label: user.name })),
      ]}
      onChange={(nextValue) => onValueChange((nextValue ?? 'all') as AssigneeFilterValue)}
      disabled={disabled}
      searchPlaceholder="Search staff"
    />
  )
}

/** Search beside one Filters button (USA-1582); every filter and saved view lives in its panel. */
export function InvoiceListFilters({
  filters,
  staff,
  count,
}: {
  filters: ReturnType<typeof useInvoiceListFilters>
  staff: ApInvoiceAssignedUser[]
  /** How many invoices match, shown at the end of the search row (USA-1634). */
  count?: string
}) {
  const {
    search,
    setSearch,
    status,
    setStatus,
    priority,
    setPriority,
    reviewQueue,
    setReviewQueue,
    archiveView,
    setArchiveView,
    linkType,
    setLinkType,
    intakeSource,
    setIntakeSource,
    dueFrom,
    setDueFrom,
    dueTo,
    setDueTo,
    assigneeFilter,
    setAssigneeFilter,
    savedViews,
    selectedSavedViewId,
    setSelectedSavedViewId,
    savedViewName,
    setSavedViewName,
    setSort,
    staffId,
    resetPage,
    activeFilterCount,
    resetFilters,
    applySavedView,
    saveCurrentView,
    deleteSelectedView,
  } = filters

  return (
    <ListFilters
      count={count}
      active={activeFilterCount}
      onClear={resetFilters}
      search={
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <SearchInput
            placeholder="Search invoice, vendor, PO, project..."
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              resetPage()
            }}
            className="pl-9"
          />
        </div>
      }
    >
      <ListFilter label="Work queue">
        <SearchSelect
          ariaLabel="Invoice work queue filter"
          className="h-9 w-full"
          value={reviewQueue === 'all' ? undefined : reviewQueue}
          allLabel="All active invoices"
          options={AP_INVOICE_REVIEW_QUEUES.map((queue) => ({ value: queue.key, label: queue.label }))}
          searchPlaceholder="Search work queues"
          onChange={(value) => {
            const queue = (value ?? 'all') as ApInvoiceReviewQueueKey | 'all'
            setReviewQueue(queue)
            if (queue === 'what_needs_paying') {
              setSort({ by: 'contractual_urgency', dir: 'asc' })
            }
            setLinkType(
              queue === 'what_needs_paying'
                ? 'project_linked'
                : queue === 'unlinked_po_project'
                  ? 'unlinked'
                  : 'all',
            )
            resetPage()
          }}
        />
      </ListFilter>
      <ListFilter label="Assignee">
        <AssigneeSelect
          value={assigneeFilter}
          onValueChange={(value) => {
            setAssigneeFilter(value)
            resetPage()
          }}
          staff={staff}
          disabled={Boolean(staffId)}
        />
      </ListFilter>
      <ListFilter label="Status">
        <SearchSelect
          ariaLabel="Invoice status filter"
          className="h-9 w-full"
          value={status === 'all' ? undefined : status}
          allLabel="All statuses"
          options={AP_INVOICE_STATUSES.map((invoiceStatus) => ({
            value: invoiceStatus,
            label: formatInvoiceStatus(invoiceStatus),
          }))}
          onChange={(value) => {
            setStatus(value ?? 'all')
            resetPage()
          }}
          searchPlaceholder="Search statuses"
        />
      </ListFilter>
      <ListFilter label="Priority">
        <Select
          value={priority}
          onValueChange={(value) => {
            setPriority(value)
            resetPage()
          }}
        >
          <SelectTrigger aria-label="Invoice priority filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {AP_INVOICE_PRIORITIES.map((invoicePriority) => (
              <SelectItem key={invoicePriority} value={invoicePriority}>
                {formatInvoicePriority(invoicePriority)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ListFilter>
      <ListFilter label="Linked to">
        <Select
          value={linkType}
          onValueChange={(value) => {
            setLinkType(value as LinkFilterValue)
            resetPage()
          }}
        >
          <SelectTrigger aria-label="Linked type filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any</SelectItem>
            <SelectItem value="project_linked">A project</SelectItem>
            <SelectItem value="po_linked">A PO</SelectItem>
            <SelectItem value="shipping_linked">A shipment</SelectItem>
            <SelectItem value="unlinked">Nothing</SelectItem>
          </SelectContent>
        </Select>
      </ListFilter>
      <ListFilter label="Came from">
        <Select
          value={intakeSource}
          onValueChange={(value) => {
            setIntakeSource(value as IntakeSourceFilterValue)
            resetPage()
          }}
        >
          <SelectTrigger aria-label="Invoice intake source filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Anywhere</SelectItem>
            <SelectItem value="manual">Manual entry</SelectItem>
            <SelectItem value="mailbox">Accounting mailbox</SelectItem>
            <SelectItem value="tracker">Historical tracker</SelectItem>
          </SelectContent>
        </Select>
      </ListFilter>
      <ListFilter label="Archived">
        <Select
          value={archiveView}
          onValueChange={(value) => {
            setArchiveView(value as 'active' | 'archived' | 'all')
            resetPage()
          }}
        >
          <SelectTrigger aria-label="Archive filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Hide archived</SelectItem>
            <SelectItem value="archived">Archived only</SelectItem>
            <SelectItem value="all">Show both</SelectItem>
          </SelectContent>
        </Select>
      </ListFilter>
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">Due between</span>
        <div className="grid grid-cols-2 gap-2">
          <Input
            aria-label="Due from"
            type="date"
            value={dueFrom}
            className="h-9 min-w-0"
            onChange={(event) => {
              setDueFrom(event.target.value)
              resetPage()
            }}
          />
          <Input
            aria-label="Due through"
            type="date"
            value={dueTo}
            min={dueFrom || undefined}
            className="h-9 min-w-0"
            onChange={(event) => {
              setDueTo(event.target.value)
              resetPage()
            }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5 border-t pt-3">
        <span className="text-xs font-medium text-muted-foreground">Saved views</span>
        {savedViews.length > 0 && (
          <SearchSelect
            ariaLabel="Saved AP view"
            className="h-9 w-full"
            value={selectedSavedViewId === 'none' ? undefined : selectedSavedViewId}
            allLabel="Choose a saved view"
            options={savedViews.map((view) => ({ value: view.id, label: view.name }))}
            onChange={(value) => {
              if (value === undefined) {
                setSelectedSavedViewId('none')
                setSavedViewName('')
                return
              }
              const view = savedViews.find((candidate) => candidate.id === value)
              if (view) applySavedView(view)
            }}
            searchPlaceholder="Search saved views"
          />
        )}
        <div className="flex items-center gap-1">
          <InlineFieldInput
            aria-label="Saved view name"
            value={savedViewName}
            onChange={(event) => setSavedViewName(event.target.value)}
            placeholder="Name these filters"
            className="h-9 min-w-0 flex-1"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={saveCurrentView}
            aria-label="Save view"
          >
            <Save className="h-4 w-4" />
          </Button>
          {selectedSavedViewId !== 'none' && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={deleteSelectedView}
              aria-label="Delete saved view"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </ListFilters>
  )
}
